/**
 * Wire types — mirror of the Worker contract in
 * `functions/api/intake/types.ts`. We intentionally redeclare rather than
 * re-export so the SPA bundle doesn't pull in Zod or the program-config
 * module graph from the Worker.
 *
 * The Worker's Zod schemas are the runtime contract: any drift here gets
 * caught at the Worker edge with a 400. Keep this file in sync when the
 * Worker contract changes.
 *
 * Canonical source: `intake/functions/api/intake/types.ts`
 *                    `intake/functions/api/intake/config.ts` (SUPPORTED_LANGUAGES)
 */

export const SUPPORTED_LANGUAGES = ['en', 'es', 'pt-BR', 'ko', 'vi', 'gn'] as const;
export type Language = (typeof SUPPORTED_LANGUAGES)[number];

export type Action =
  | 'upload'
  | 'confirm'
  | 'chat'
  | 'field_edit'
  | 'submit'
  | 'detect_language';

export interface HistoryTurn {
  role: 'user' | 'assistant';
  content: string;
}

export interface IntakeState {
  /** field-key → confirmed value (type varies per field schema) */
  confirmed: Record<string, unknown>;
  /** field keys still required, in canonical order */
  required_pending: string[];
  /** field keys still optional, in canonical order */
  optional_pending: string[];
  /** chat history sent on every turn; Worker may return a compressed version */
  history: HistoryTurn[];
  /** set by detect_language action; downstream handlers default to 'en' when absent */
  language?: Language;
}

export interface IntakeRequest<P = unknown> {
  action: Action;
  session_id: string;
  state: IntakeState;
  payload: P;
}

/** Constrained-field question rendered by the chat UI. Present when the next
 *  pending field has a type that maps to a specific response component.
 *
 *  Component selection:
 *   'text'    → TextResponse  (full_name, address, city, state, zip, county, ssn_last_four)
 *   'select'  → SelectResponse (preferred_language, reason_for_visit, employment_status,
 *                               education_level, ethnicity, sex_at_birth, gender,
 *                               disability_status, veteran_status)
 *   'multi'   → MultiResponse  (race)
 *   'boolean' → BooleanResponse (msfw_status, new_american_status,
 *                               rapid_response_attendance, consent_agreement)
 *   'date'    → DateResponse   (dob)
 *   'number'  → NumberResponse (future income/hours fields)
 */
export interface Question {
  field: string;
  /** Translated field label for the active language. */
  label: string;
  /**
   * Full question sentence for the active language — rendered as the heading
   * above the chat response control. Falls back to `label` when absent.
   */
  askPrompt?: string;
  type: 'text' | 'select' | 'multi' | 'boolean' | 'date' | 'number';
  /** Rendered option labels in the active language; empty for non-choice types. */
  options: Array<{ key: string; label: string }>;
  /** select/multi: 'chips' for short option sets; defaults to 'stack'. */
  layout?: 'stack' | 'chips';
  /** text: keyboard and browser-autofill hints. */
  inputHints?: {
    autoComplete?: string;
    /** Valid HTML inputmode values (excludes 'none' which React doesn't accept on input). */
    inputMode?: 'text' | 'search' | 'email' | 'tel' | 'url' | 'numeric' | 'decimal';
    maxLength?: number;
  };
  /** boolean: when true, requires a second tap to confirm (e.g. consent_agreement). */
  staged?: boolean;
  /** date: ISO YYYY-MM-DD bounds forwarded to the native date picker. */
  dateMin?: string;
  dateMax?: string;
  /** number: range and display. */
  numberMin?: number;
  numberMax?: number;
  step?: number;
  prefix?: string;
  suffix?: string;
}

export interface IntakeResponse {
  action: Action;
  state_delta: Partial<IntakeState>;
  reply: string | null;
  question: Question | null;
  progress: { confirmed: number; required_total: number };
  complete: boolean;
  extras?: {
    /** from `upload` — Gemma-extracted fields, await user confirmation */
    extracted?: Record<string, unknown>;
    /** from `detect_language` — echoed so client can cache outside of state */
    active_language?: Language | 'unsupported';
    /** from `chat` — glossary keys Gemma flagged in the reply */
    terms_used?: string[];
  };
}

// ---------------------------------------------------------------------------
// Action-specific payload shapes (request side).
// ---------------------------------------------------------------------------

export interface UploadPayload {
  /** base64-encoded document bytes (JPEG after HEIC→JPEG client-side conversion) */
  document: string;
  /** MIME type of the encoded bytes */
  mime: string;
}

export interface ConfirmPayload {
  /** field-key → value, accepted as-is or corrected by the user. */
  confirmed: Record<string, unknown>;
  /** field keys to discard (OCR extracted them but user removed them). */
  rejected: string[];
}

export type ChatPayload =
  | { message: string; answer?: undefined }
  | {
      message?: undefined;
      answer: { field: string; value: unknown };
    };

export interface FieldEditPayload {
  field: string;
  value: unknown;
}

export interface DetectLanguagePayload {
  /** the user's free-text response in their preferred language */
  message: string;
}

export type SubmitPayload = Record<string, never>;
